import React from "react";
import { Box, Paper, Typography, Grid, Divider } from "@mui/material";
import NodeViewer from "./NodeViewer";
import LabReportGrid from "./LabReportGrid";
import NodesArrayViewer from "./NodesArrayViewer";

const LabFilingDetails = ({ lab }) => {
  if (!lab) return null;

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Grid
        container
        spacing={2}
        sx={{
          mb: 2,
          alignItems: "stretch",
        }}
      >
        {/* Column 1: Patient Data */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            pr: 2,
            borderRight: "2px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 1,
              borderBottom: "2px solid",
              borderColor: "primary.main",
              display: "inline-block",
              pb: 0.5,
            }}
          >
            Patient Data
          </Typography>

          <Typography variant="body2">
            <strong>EMIS ID:</strong> {lab.patient_data?.emis_id}
          </Typography>
          <Typography variant="body2">
            <strong>NHS Number:</strong> {lab.patient_data?.nhs_num}
          </Typography>
          <Typography variant="body2">
            <strong>Received Date:</strong> {lab.patient_data?.received_date}
          </Typography>
          <Typography variant="body2">
            <strong>Issue Date:</strong> {lab.patient_data?.issue_date}
          </Typography>
          <Typography variant="body2">
            <strong>Gender:</strong> {lab.patient_data?.gender}
          </Typography>
          <Typography variant="body2">
            <strong>Age:</strong> {lab.patient_data?.age}
          </Typography>
          <Typography variant="body2">
            <strong>Report ID:</strong> {lab.patient_data?.lab_report_id}
          </Typography>
          <Typography variant="body2">
            <strong>Report Type:</strong> {lab.patient_data?.report_type}
          </Typography>
          <Typography variant="body2">
            <strong>Approval Status:</strong>{" "}
            <Box
              component="span"
              sx={{
                color: lab.approved ? "#388e3c" : "#f57c00",
                fontWeight: "bold",
              }}
            >
              {lab.approved ? "Approved" : "Not Approved"}
            </Box>
          </Typography>
        </Grid>

        {/* Column 2: Patient Profile Summary */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            px: 2,
            borderRight: "2px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 1,
              borderBottom: "2px solid",
              borderColor: "primary.main",
              display: "inline-block",
              pb: 0.5,
            }}
          >
            Patient Profile Summary
          </Typography>

          <Box sx={{ mt: 1 }}>
            {lab.patient_profile?.health_status &&
              Object.entries(lab.patient_profile.health_status).map(
                ([key, val]) => (
                  <Typography key={key} variant="body2">
                    <strong>{key}:</strong> {val?.value || "-"}{" "}
                    {val?.date ? `(${val.date})` : ""}
                  </Typography>
                ),
              )}
          </Box>
        </Grid>

        {/* Column 3: Medications */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            px: 2,
            borderRight: "2px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 1,
              borderBottom: "2px solid",
              borderColor: "primary.main",
              display: "inline-block",
              pb: 0.5,
            }}
          >
            Medications
          </Typography>

          <Box sx={{ mt: 1, maxWidth: 300 }}>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              <strong>Acute:</strong>{" "}
              {lab.patient_profile?.medications?.acute?.length
                ? lab.patient_profile.medications.acute.join(", ")
                : "None"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              <strong>Repeat:</strong>{" "}
              {lab.patient_profile?.medications?.repeat?.length
                ? lab.patient_profile.medications.repeat.join(", ")
                : "None"}
            </Typography>
          </Box>
        </Grid>

        {/* Column 4: Problems */}
        <Grid
          item
          xs={12}
          md={3}
          sx={{
            pl: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: "primary.main",
              fontWeight: "bold",
              mb: 1,
              borderBottom: "2px solid",
              borderColor: "primary.main",
              display: "inline-block",
              pb: 0.5,
            }}
          >
            Problems
          </Typography>

          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
              Active
            </Typography>
            {lab.patient_profile?.problems?.active &&
            Object.keys(lab.patient_profile.problems.active).length > 0 ? (
              Object.entries(lab.patient_profile.problems.active).map(
                ([problem, date]) => (
                  <Typography key={problem} variant="body2">
                    {problem} {date ? `(${date})` : ""}
                  </Typography>
                ),
              )
            ) : (
              <Typography variant="body2">None</Typography>
            )}

            <Typography variant="body2" sx={{ fontWeight: "bold", mt: 1 }}>
              Past
            </Typography>
            {lab.patient_profile?.problems?.past?.length ? (
              lab.patient_profile.problems.past.map((p, i) => (
                <Typography key={i} variant="body2">
                  {p}
                </Typography>
              ))
            ) : (
              <Typography variant="body2">None</Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <LabReportGrid report={lab.patient_data?.report_data} />

      {Array.isArray(lab.categories) && lab.categories.length > 0 ? (
        lab.categories.map((cat, idx) => (
          <Paper
            key={idx}
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              borderLeft: "5px solid #1976d2",
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              {cat.category_name}
            </Typography>

            {cat.current_ruleset ? (
              <Box sx={{ pl: 2 }}>
                <NodeViewer
                  root={cat.current_ruleset}
                  nodeConfigs={{}}
                  onSelectNode={() => {}}
                  selectedNodeId={null}
                />
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No ruleset available.
              </Typography>
            )}
          </Paper>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No categories found in this report.
        </Typography>
      )}

      <NodesArrayViewer
        nodes={lab.actions}
        nodeConfigs={{}}
        onSelectNode={() => {}}
        selectedNodeId={null}
      />
    </Paper>
  );
};

export default LabFilingDetails;
